// Renders a serialized Oscar account/auth form (build_form_fields +
// separately-computed labels/errors) as a native React field tree: plain fields
// with the label above the input (no floating-label placeholder widget). Hidden
// fields emit a bare control with no wrapper/label.
import { Immutable } from "immer";
import React from "react";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { FormErrorList } from "../../../forms/FormErrorList";
import { FieldControl, FormFieldData } from "../../../forms/FormPageFields";
import {
    FORM_FIELD_CLASS_NAME,
    FORM_FIELD_REQUIRED_CLASS_NAME,
    FORM_LABEL_CLASS_NAME,
} from "../../../forms/classNames";

type IProps = Immutable<{
    fields: FormFieldData[];
    field_labels: Record<string, string>;
    field_errors: Record<string, readonly string[]>;
    non_field_errors: readonly string[];
    /** Echo each label as its control's placeholder (default). Cocoon's
     * loadup form never rendered placeholders, so it opts out. */
    showPlaceholders?: boolean;
    /** Extra content rendered inside a field's wrapper, below its error list
     * (e.g. the duplicate-email log-in/reset affordance under `email`). */
    afterField?: Partial<Record<string, React.ReactNode>>;
}>;

export const FormFields = (props: IProps): React.JSX.Element => {
    const showPlaceholders = props.showPlaceholders ?? true;
    return (
        <>
            {props.non_field_errors.length > 0 && (
                <div className={FORM_FIELD_CLASS_NAME}>
                    <FormErrorList errors={props.non_field_errors} />
                </div>
            )}
            {props.fields.map((field) => {
                if (field.kind === "hidden") {
                    return <FieldControl key={field.field_id} field={field} />;
                }
                const errors = props.field_errors[field.name] ?? [];
                const label = props.field_labels[field.name] ?? field.name;
                return (
                    <div
                        className={concatClassNames([
                            FORM_FIELD_CLASS_NAME,
                            field.required
                                ? FORM_FIELD_REQUIRED_CLASS_NAME
                                : null,
                        ])}
                        data-name={field.name}
                        key={field.field_id}
                    >
                        <label
                            className={FORM_LABEL_CLASS_NAME}
                            htmlFor={field.field_id}
                        >
                            {label}
                        </label>
                        <FieldControl
                            field={field}
                            placeholder={showPlaceholders ? label : undefined}
                        />
                        {errors.length > 0 && <FormErrorList errors={errors} />}
                        {props.afterField?.[field.name]}
                    </div>
                );
            })}
        </>
    );
};
